import { useMemo, useState } from "react";

/**
 * Single-line topology with optional DOE heat-map overlay.
 *
 * Props:
 *  - topology: { network_id, buses, branches, bbox, counts }
 *  - doeResult: optional { meta, time_series, per_bus, bus_summary }
 */

const ROLE_STYLES = {
  slack: { fill: "#D32F2F", stroke: "#7F1D1D" },
  gen: { fill: "#16A34A", stroke: "#14532D" },
  load: { fill: "#002FA7", stroke: "#001F7A" },
  junction: { fill: "#52525b", stroke: "#27272a" },
};

const VIEW_MODES = [
  { value: "topology", label: "Topology" },
  { value: "import", label: "Import" },
  { value: "export", label: "Export" },
];

const T_AGGREGATE = -1; // "Avg" sentinel for the time-step slider
const SVG_WIDTH = 1100;
const SVG_MIN_HEIGHT = 360;
const SVG_MAX_HEIGHT = 720;
const SVG_PAD = 48;

// Diverging colour ramp: 0 MW = dark red, mid = amber, max = green.
// Returns a hex string.
function headroomColor(value, maxValue) {
  if (!Number.isFinite(value) || maxValue <= 0) return "#9ca3af";
  const t = Math.max(0, Math.min(1, value / maxValue));
  // 0 → #B91C1C (red), 0.5 → #F59E0B (amber), 1 → #16A34A (green)
  const stops = [
    { t: 0, c: [185, 28, 28] },
    { t: 0.5, c: [245, 158, 11] },
    { t: 1, c: [22, 163, 74] },
  ];
  let a = stops[0];
  let b = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (t >= stops[i].t && t <= stops[i + 1].t) {
      a = stops[i];
      b = stops[i + 1];
      break;
    }
  }
  const span = b.t - a.t || 1;
  const k = (t - a.t) / span;
  const rgb = a.c.map((v, i) => Math.round(v + (b.c[i] - v) * k));
  return `#${rgb.map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

export default function NetworkTopology({ topology, doeResult }) {
  const [hovered, setHovered] = useState(null);
  const [view, setView] = useState("topology");
  const [step, setStep] = useState(T_AGGREGATE);

  const heatmapAvailable = Boolean(doeResult);
  const currentView = !heatmapAvailable && view !== "topology" ? "topology" : view;
  const isHeatmap = currentView !== "topology" && heatmapAvailable;

  // Build per-bus headroom lookup
  const headroom = useMemo(() => {
    if (!doeResult) return null;
    const direction = currentView === "import" ? "import_mw" : "export_mw";
    const byBus = new Map();
    for (const r of doeResult.per_bus) {
      const arr = byBus.get(r.bus) || [];
      arr[r.time_step] = r[direction];
      byBus.set(r.bus, arr);
    }
    // Build per-step / avg map
    const map = new Map();
    for (const [bus, arr] of byBus.entries()) {
      const valid = arr.filter((v) => Number.isFinite(v));
      const avg = valid.length ? valid.reduce((s, v) => s + v, 0) / valid.length : 0;
      map.set(bus, { perStep: arr, avg });
    }
    return map;
  }, [doeResult, currentView]);

  const maxHeadroom = useMemo(() => {
    if (!headroom) return 0;
    let m = 0;
    for (const v of headroom.values()) {
      m = Math.max(m, v.avg, ...v.perStep.filter((x) => Number.isFinite(x)));
    }
    return m;
  }, [headroom]);

  const { busesPx, branchesPx, viewBox, width, height } = useMemo(() => {
    if (!topology) {
      return {
        busesPx: [],
        branchesPx: [],
        viewBox: "0 0 100 100",
        width: 100,
        height: 100,
      };
    }
    const { xmin, xmax, ymin, ymax } = topology.bbox;
    const xRange = Math.max(Math.abs(xmax - xmin), 1);
    const yRange = Math.max(Math.abs(ymax - ymin), 1);
    const innerWidth = SVG_WIDTH - 2 * SVG_PAD;
    const idealHeight = SVG_PAD * 2 + (yRange / xRange) * innerWidth;
    const H = Math.max(SVG_MIN_HEIGHT, Math.min(SVG_MAX_HEIGHT, idealHeight));
    const innerHeight = H - 2 * SVG_PAD;
    const scale = Math.min(innerWidth / xRange, innerHeight / yRange);
    const plotWidth = xRange * scale;
    const plotHeight = yRange * scale;
    const offsetX = (SVG_WIDTH - plotWidth) / 2;
    const offsetY = (H - plotHeight) / 2;

    const toScreen = (point) => ({
      px: offsetX + (Number(point.x) - xmin) * scale,
      py: offsetY + (ymax - Number(point.y)) * scale,
    });

    const busesPx = topology.buses.map((b) => ({ ...b, ...toScreen(b) }));
    const byIdx = new Map(busesPx.map((b) => [b.idx, b]));
    const branchesPx = topology.branches
      .map((br) => {
        const rawPoints = Array.isArray(br.points)
          ? br.points
              .map((point) => {
                if (Array.isArray(point)) return { x: Number(point[0]), y: Number(point[1]) };
                return { x: Number(point?.x), y: Number(point?.y) };
              })
              .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
          : [];
        if (rawPoints.length >= 2) {
          const points = rawPoints.map((point) => toScreen(point));
          const first = points[0];
          const last = points[points.length - 1];
          return {
            ...br,
            points,
            pointsAttr: points.map((point) => `${point.px},${point.py}`).join(" "),
            x1: first.px,
            y1: first.py,
            x2: last.px,
            y2: last.py,
          };
        }
        const a = byIdx.get(br.from);
        const c = byIdx.get(br.to);
        if (!a || !c) return null;
        const points = [
          { px: a.px, py: a.py },
          { px: c.px, py: c.py },
        ];
        return {
          ...br,
          points,
          pointsAttr: points.map((point) => `${point.px},${point.py}`).join(" "),
          x1: a.px,
          y1: a.py,
          x2: c.px,
          y2: c.py,
        };
      })
      .filter(Boolean);
    return { busesPx, branchesPx, viewBox: `0 0 ${SVG_WIDTH} ${H}`, width: SVG_WIDTH, height: H };
  }, [topology]);

  if (!topology) return null;

  const stepLabel =
    step === T_AGGREGATE
      ? "Avg"
      : `Step ${step}` +
        (doeResult?.time_series?.[step]?.hour != null
          ? ` · ${String(doeResult.time_series[step].hour).padStart(2, "0")}:00`
          : "");

  const headroomFor = (busIdx) => {
    if (!headroom) return null;
    const e = headroom.get(busIdx);
    if (!e) return null;
    return step === T_AGGREGATE ? e.avg : e.perStep[step] ?? 0;
  };

  return (
    <div
      className="border border-border rounded-[2px] bg-background overflow-hidden"
      data-layout={topology.geo ? "geodata" : "computed"}
      data-testid="topology-diagram"
    >
      <div className="px-5 py-3 border-b border-border bg-secondary/40 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
            Topology Viewer
            {isHeatmap && (
              <span className="ml-2 text-foreground">
                · {currentView} heat-map · {stepLabel}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 font-mono">
            {topology.network_id} · {topology.counts.buses} buses ·{" "}
            {topology.counts.lines} lines · {topology.counts.trafos} trafos ·{" "}
            {topology.geo ? "x/y geodata" : "computed layout"}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* View toggle */}
          <div
            className="inline-flex border border-border rounded-[2px] overflow-hidden"
            data-testid="topology-view-toggle"
          >
            {VIEW_MODES.map((m) => {
              const active = currentView === m.value;
              const disabled = m.value !== "topology" && !heatmapAvailable;
              return (
                <button
                  key={m.value}
                  type="button"
                  data-testid={`topology-view-${m.value}`}
                  disabled={disabled}
                  onClick={() => setView(m.value)}
                  className={[
                    "px-3 h-7 text-[10px] uppercase tracking-[0.18em] font-semibold font-mono transition-colors",
                    active
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-muted-foreground hover:bg-secondary",
                    disabled ? "opacity-40 cursor-not-allowed" : "",
                  ].join(" ")}
                >
                  {m.label}
                </button>
              );
            })}
          </div>

          {isHeatmap ? <ColorbarLegend max={maxHeadroom} /> : <RoleLegend />}
        </div>
      </div>

      {/* Time-step slider (heat-map mode only) */}
      {isHeatmap && doeResult.time_series.length > 0 && (
        <div
          className="px-5 py-3 border-b border-border bg-background flex items-center gap-4"
          data-testid="topology-step-slider"
        >
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground w-12">
            Step
          </span>
          <input
            type="range"
            min={T_AGGREGATE}
            max={doeResult.time_series.length - 1}
            value={step}
            onChange={(e) => setStep(Number(e.target.value))}
            className="flex-1 accent-[#002FA7]"
            data-testid="topology-step-input"
          />
          <span className="font-mono text-xs w-24 text-right" data-testid="topology-step-label">
            {stepLabel}
          </span>
        </div>
      )}

      <div className="relative w-full overflow-x-auto">
        <svg
          viewBox={viewBox}
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          style={{ minWidth: 680, maxHeight: SVG_MAX_HEIGHT, aspectRatio: `${width} / ${height}` }}
          data-testid="topology-svg"
        >
          {/* branches */}
          {branchesPx.map((br) => {
            const isTrafo = br.kind === "trafo";
            return (
              <g key={br.id} data-testid={`branch-${br.id}`}>
                {!isTrafo && (
                  <polyline
                    points={br.pointsAttr}
                    fill="none"
                    stroke="var(--branch-line)"
                    strokeWidth="1.25"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                )}
                {isTrafo && (
                  <>
                    <line
                      x1={br.x1}
                      y1={br.y1}
                      x2={br.x2}
                      y2={br.y2}
                      stroke="#D32F2F"
                      strokeWidth="2.2"
                      strokeDasharray="6 3"
                    />
                    <g transform={`translate(${(br.x1 + br.x2) / 2}, ${(br.y1 + br.y2) / 2})`}>
                      <circle r="6" fill="var(--background)" stroke="#D32F2F" strokeWidth="1.6" />
                      <circle r="6" cx="6" fill="var(--background)" stroke="#D32F2F" strokeWidth="1.6" />
                    </g>
                  </>
                )}
              </g>
            );
          })}
          {/* buses */}
          {busesPx.map((b) => {
            const roleStyle = ROLE_STYLES[b.role] || ROLE_STYLES.junction;
            const isHover = hovered === b.idx;
            const isLoadBus = headroom?.has(b.idx);
            // In heat-map mode we recolour ONLY load buses (which have envelope entries).
            // Other roles keep their categorical colour but are slightly dimmed.
            let fill = roleStyle.fill;
            let stroke = roleStyle.stroke;
            let dim = false;
            if (isHeatmap) {
              if (isLoadBus) {
                const v = headroomFor(b.idx);
                fill = headroomColor(v, maxHeadroom);
                stroke = "#0b0b0c";
              } else {
                dim = true;
              }
            }
            const baseR = b.role === "junction" ? 4 : 7;
            const r = isHeatmap && isLoadBus ? 9 : baseR;
            const rHover = r + 3;
            return (
              <g
                key={b.idx}
                data-testid={`bus-node-${b.idx}`}
                data-role={b.role}
                data-headroom={isHeatmap && isLoadBus ? headroomFor(b.idx) : undefined}
                onMouseEnter={() => setHovered(b.idx)}
                onMouseLeave={() => setHovered((h) => (h === b.idx ? null : h))}
                style={{ cursor: "default", opacity: dim ? 0.35 : 1 }}
              >
                <circle
                  cx={b.px}
                  cy={b.py}
                  r={isHover ? rHover : r}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1.4"
                  style={{ transition: "r 120ms ease, fill 150ms ease" }}
                />
              </g>
            );
          })}
          {/* tooltip */}
          {hovered != null && (() => {
            const b = busesPx.find((x) => x.idx === hovered);
            if (!b) return null;
            const lines = [`${b.name} · ${b.vn_kv.toFixed(1)} kV · ${b.role}`];
            if (isHeatmap && headroom?.has(b.idx)) {
              const v = headroomFor(b.idx);
              lines.push(
                `${currentView}: ${Number.isFinite(v) ? v.toFixed(2) : "—"} MW (${stepLabel})`,
              );
            }
            const w = Math.max(...lines.map((l) => l.length)) * 6.2 + 16;
            const h = 16 + 14 * lines.length;
            const tx = Math.min(Math.max(b.px - w / 2, 8), Math.max(8, width - w - 8));
            const ty = Math.max(b.py - h - 14, 8);
            return (
              <g pointerEvents="none" data-testid="topology-tooltip">
                <rect x={tx} y={ty} width={w} height={h} rx="2" fill="var(--tooltip-bg)" opacity="0.95" />
                {lines.map((l, i) => (
                  <text
                    key={i}
                    x={tx + w / 2}
                    y={ty + 15 + i * 14}
                    textAnchor="middle"
                    fontSize="11"
                    fontFamily="IBM Plex Mono, monospace"
                    fill="var(--tooltip-text)"
                  >
                    {l}
                  </text>
                ))}
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}

function RoleLegend() {
  return (
    <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
      <LegendDot color="#D32F2F" label="Slack" />
      <LegendDot color="#16A34A" label="Gen" />
      <LegendDot color="#002FA7" label="Load" />
      <LegendDot color="#52525b" label="Junction" />
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block w-5 border-t border-dashed"
          style={{ borderColor: "#D32F2F", borderTopWidth: 2 }}
        />
        Trafo
      </span>
    </div>
  );
}

function ColorbarLegend({ max }) {
  return (
    <div
      className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground font-mono"
      data-testid="topology-colorbar"
    >
      <span>0 MW</span>
      <span
        className="inline-block w-32 h-2 rounded-sm"
        style={{
          background:
            "linear-gradient(90deg, #B91C1C 0%, #F59E0B 50%, #16A34A 100%)",
        }}
      />
      <span>{max ? `${max.toFixed(1)} MW` : "—"}</span>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="inline-block size-2.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
